import React, {useEffect, useState} from 'react';
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import RepositionBlackIcon from "../../../../assets/images/icon_reposition_black.svg";
import {useTranslation} from "react-i18next";
import {ActionButton} from '../GenerateImageButton';
import {useMobile} from "../../../../context/MobileContext";
import {useTemplate} from "../../../../context/TemplateContext";
import {isUserLayerId} from "../../../../scene/sceneToKonva";
import {ImageInput} from "../../../../models/Template";

// Types
type FloatingImageControlBarProps = {
    onClose: () => void;
    onRepositionImage: () => void;
    onResetImage: () => void;
    onApprove: () => void;
    onZoom?: (direction: 1 | -1) => void;
    activeMode?: 'select' | 'transform';
    showSecondStage?: boolean;
    selectedDesign: number;
};

// Styled components
const FloatingBarContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({theme, isMobile}) => ({
    position: !isMobile ? 'fixed' : 'unset',
    bottom: '2rem',
    left: '50%',
    transform: !isMobile ? 'translateX(-50%)' : undefined,
    backgroundColor: 'white',
    borderRadius: '15px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    display: !isMobile ? 'flex' : 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    zIndex: 1000,
    transition: 'all 0.3s ease',
    marginTop: !isMobile ? undefined : "16px",
}));

const VerticalDivider = styled(Divider)(({theme}) => ({
    height: '30px',
    margin: 'auto 8px',
    backgroundColor: '#051624',
}));

const BarButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({theme, isMobile}) => ({
    color: '#051624',
    fontSize: isMobile ? "14px" : "16px",
    lineHeight: isMobile ? 1.2 : undefined
}));


/**
 * Floating control bar for image manipulation
 */
const FloatingImageControlBar: React.FC<FloatingImageControlBarProps> = ({
                                                                             onClose,
                                                                             onRepositionImage,
                                                                             onResetImage,
                                                                             onApprove,
                                                                             onZoom,
                                                                             activeMode = 'select',
                                                                             selectedDesign
                                                                         }) => {
    const {t} = useTranslation()
    const {isMobile} = useMobile();
    const [allowImageTransform, setAllowImageTransform] = useState(false)
    const {template, selectedImageData} = useTemplate()

    useEffect(() => {
        if (selectedDesign >= 0 && template?.designs?.[selectedDesign].key && selectedImageData?.value) {
            // A USER LAYER has no template input and therefore no `size` entry —
            // the old lookup returned undefined and silently hid Reposition, the
            // one control a layer image most needs.
            if (isUserLayerId(selectedImageData.elementKey)) {
                setAllowImageTransform(true)
                return
            }
            const selectedDesignKey = template.designs[selectedDesign].key
            const selectedImageInput = template.inputs[selectedImageData.elementKey] as ImageInput
            setAllowImageTransform(selectedImageInput?.size?.[selectedDesignKey]?.width != undefined && selectedImageInput?.size?.[selectedDesignKey]?.height != undefined)
        } else {
            setAllowImageTransform(false)
        }
    }, [selectedDesign, selectedImageData, template])

    // First stage shows Select from Library and Reposition options
    const renderFirstStage = () => (
        <>
            {allowImageTransform && <>
                <BarButton isMobile={isMobile}
                           variant={'text'}
                           onClick={onRepositionImage}>
                    <img src={RepositionBlackIcon} alt="Reposition Icon" style={{
                        height: '25px',
                        position: 'relative',
                        marginLeft: 2
                    }}/>
                    {t("editor.floatingImageControlBar.reposition")}
                </BarButton>
                <VerticalDivider orientation="vertical" flexItem/>
            </>}
            <BarButton isMobile={isMobile}
                       variant={'text'}
                       onClick={onClose}
            >
                {t("editor.floatingImageControlBar.close")}
            </BarButton>
        </>
    );

    // Second stage (reposition active): zoom +/-, Reset, Approve. The "Reposition"
    // label is dropped here — you're already repositioning, and on mobile it made
    // the row overflow.
    const renderSecondStage = () => (
        <>
            {onZoom && (
                <>
                    <BarButton isMobile={isMobile}
                               variant={'text'}
                               title={t("editor.floatingImageControlBar.zoomOut")}
                               aria-label={t("editor.floatingImageControlBar.zoomOut")}
                               sx={{minWidth: 0, fontSize: isMobile ? '20px' : '22px', lineHeight: 1, px: isMobile ? 0.25 : 1}}
                               onClick={() => onZoom(-1)}>
                        −
                    </BarButton>
                    <BarButton isMobile={isMobile}
                               variant={'text'}
                               title={t("editor.floatingImageControlBar.zoomIn")}
                               aria-label={t("editor.floatingImageControlBar.zoomIn")}
                               sx={{minWidth: 0, fontSize: isMobile ? '20px' : '22px', lineHeight: 1, px: isMobile ? 0.25 : 1}}
                               onClick={() => onZoom(1)}>
                        +
                    </BarButton>
                    <VerticalDivider orientation="vertical" flexItem/>
                </>
            )}
            <BarButton isMobile={isMobile}
                       variant={'text'}
                       onClick={() => onResetImage()}>
                {t("editor.floatingImageControlBar.reset")}
            </BarButton>
            <ActionButton onClick={onApprove} variant="contained"
                          sx={{
                              fontSize: isMobile ? "14px" : "16px"
                          }}>
                {t("editor.floatingImageControlBar.approve")}
            </ActionButton>
        </>
    );

    return (
        <Fade in={true}>
            <FloatingBarContainer isMobile={isMobile}>
                {activeMode == 'select' ? renderFirstStage() : renderSecondStage()}
            </FloatingBarContainer>
        </Fade>
    );
};

export default FloatingImageControlBar;